import { useCallback, useEffect, useRef, useState } from "react";
import { Download, KeyRound, Loader2, PackageCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

/** Builds the signed .aab here, from this app's own manifest — no PWABuilder round trip. */
export const AndroidPackagePanel = ({ app, onNotice, onBuilt }) => {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/mobile-apps/${app.id}/android-package`);
      setJob(data);
      return data;
    } catch (e) {
      setError(errMsg(e));
      return null;
    }
  }, [app.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (job?.status !== "building") return undefined;
    timer.current = setTimeout(async () => {
      const next = await load();
      if (next?.status === "ready") { onNotice?.("Android package built."); onBuilt?.(); }
    }, 5000);
    return () => clearTimeout(timer.current);
  }, [job, load, onNotice, onBuilt]);

  const build = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/admin/mobile-apps/${app.id}/android-package`);
      setJob({ ...(job || {}), status: "building" });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/admin/mobile-apps/${app.id}/android-package.zip`,
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${app.app_id}-android.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const building = job?.status === "building";
  const ready = job?.status === "ready";
  const hasZip = Boolean(job?.has_zip);

  return (
    <div data-testid="android-package" className="rounded-xl border border-ink-600 bg-ink-800 p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-display text-lg text-slate-100">
            <PackageCheck className="h-4 w-4 text-gold" /> Android package
          </p>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Builds the signed <span className="font-mono text-[11px]">.aab</span> for Play and an{" "}
            <span className="font-mono text-[11px]">.apk</span> for testing, using this app's own
            manifest, name and icon — so it can never come out as another app. The signing key is
            kept here and reused, which is what lets you publish updates later.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="android-build" disabled={busy || building} onClick={build}
            className="bg-gold text-ink-900 hover:bg-gold-light">
            {busy || building ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              : <PackageCheck className="mr-2 h-4 w-4" />}
            {building ? "Building…" : ready ? "Build again" : "Build the package"}
          </Button>
          {hasZip && (
            <Button data-testid="android-download" variant="outline" disabled={busy}
              onClick={download}>
              <Download className="mr-2 h-4 w-4" /> Download zip
            </Button>
          )}
        </div>
      </div>

      {error && <p data-testid="android-error" className="mt-3 text-sm text-red-400">{error}</p>}
      {job?.status === "failed" && !error && (
        <p data-testid="android-failed" className="mt-3 text-sm text-red-400">{job.error}</p>
      )}

      <p data-testid="android-status" className="mt-4 text-sm text-slate-300">
        {building && "Building on PWABuilder's Bubblewrap service — this takes about a minute."}
        {!building && hasZip && `Version code ${job.version_code} · ${
          Math.round((job.size || 0) / 1024)} KB · ${(job.files || []).length} files in the zip.`}
        {!building && !hasZip && job?.status !== "failed" && "Not built yet."}
      </p>

      {hasZip && (
        <div className="mt-3 space-y-3">
          <div className="rounded-lg border border-ink-600 bg-ink-900/70 p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
              Upload key SHA-256 (registered for you)
            </p>
            <p data-testid="android-fingerprint" className="mt-1 break-all font-mono text-[11px] text-slate-200">
              {job.fingerprint || "—"}
            </p>
          </div>
          <p className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-300">
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Keep <span className="font-mono text-[11px]">signing.keystore</span> and{" "}
              <span className="font-mono text-[11px]">signing-key-info.txt</span> from the zip. A
              copy is stored here so rebuilds match, but back yours up too — Play will not accept an
              update signed with a different key.
            </span>
          </p>
        </div>
      )}
    </div>
  );
};
